import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CloudQueueIcon from "@material-ui/icons/CloudQueue";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import PageHelpGuide from "../PageHelpGuide";
import { CAMPAIGNS_HELP_DATA } from "../../constants/pageHelp/campaignsHelp";
import Chip from '@material-ui/core/Chip';
import RepeatIcon from '@material-ui/icons/Repeat';
import SpeedIcon from '@material-ui/icons/Speed';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { isNil } from "lodash";
import { i18n } from "../../translate/i18n";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { evaluate5MessagesVariation } from "../../utils/textSimilarity";
import {
  Box,
  Paper,
  InputAdornment,
  useTheme,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  MenuItem,
  Select,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
  Card,
  CardContent,
  Checkbox,
  FormGroup,
  Tooltip,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";
import StandardTabs from "../StandardTabs";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#ffffff !important",
    overflow: "hidden !important",
    display: "flex !important",
    flexDirection: "column !important",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  dialogContentScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto !important",
  },
  dialogActionsPadded: {
    padding: "16px 24px",
    flexShrink: 0,
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  recurrenceCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  previewCard: {
    marginTop: theme.spacing(1),
    maxHeight: 200,
    overflow: 'auto',
  },
  recurrenceIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));


// No CampaignModal.js - Atualizar o schema de validação

const getCampaignSchema = (sendMode) => Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required(i18n.t("campaigns.validation.recurrenceType")),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, i18n.t("campaigns.validation.recurrenceIntervalMin")).required(i18n.t("campaigns.validation.recurrenceInterval")),
    otherwise: Yup.number().nullable()
  }),
  // CORREÇÃO: Validar como array mas permitir vazio
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, i18n.t("campaigns.validation.recurrenceDaysOfWeek")).required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, i18n.t("campaigns.validation.recurrenceDayOfMonthRange")).max(31, i18n.t("campaigns.validation.recurrenceDayOfMonthRange")).required(i18n.t("campaigns.validation.recurrenceDayOfMonth")),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date().when('isRecurring', {
    is: true,
    then: Yup.date().min(new Date(), i18n.t("campaigns.validation.recurrenceEndDate")).nullable(),
    otherwise: Yup.date().nullable()
  }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, i18n.t("campaigns.validation.maxExecutions")).nullable(),
    otherwise: Yup.number().nullable()
  }),
  message1: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === true || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje 1 es obligatorio").min(20, "Mínimo 20 caracteres")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  message2: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === true || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje 2 es obligatorio").min(20, "Mínimo 20 caracteres")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  message3: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === true || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje 3 es obligatorio").min(20, "Mínimo 20 caracteres")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  message4: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === true || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje 4 es obligatorio").min(20, "Mínimo 20 caracteres")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  message5: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === true || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje 5 es obligatorio").min(20, "Mínimo 20 caracteres")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  confirmationMessage1: Yup.string().when(['confirmation', 'sendMode'], (confirmation, sendMode, schema) => {
    if (confirmation === false || sendMode === "official") return schema.nullable();
    return schema.nullable().required("El mensaje rompehielo es obligatorio")
      .test(
        "has-spintax",
        "🛑 ALTO RIESGO: El sistema exige el uso de Spintax (ej: {Hola|Buen día}) para proteger el servidor y tu número contra baneos.",
        value => !value || (value.includes("{") && value.includes("}") && value.includes("|"))
      );
  }),
  templateId: sendMode === "official" ? Yup.string().nullable().required("Debes seleccionar una plantilla de Meta") : Yup.string().nullable()
});

// [ANTIGRAVITY] Fix: Ya no dividimos el mensaje en la Vista Previa, 
// pues todo va en un solo Caption junto a la imagen.
const splitCampaignMessage = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return [""];

  const chunks = [trimmed];

  // Text Salting
  if (chunks.length > 0) {
    const zeroWidthChars = ['\u200B', '\u200C', '\u200D'];
    let salt = '';
    const saltLength = Math.floor(Math.random() * 8) + 4; // 4 a 11 caracteres invisibles
    for (let i = 0; i < saltLength; i++) {
      salt += zeroWidthChars[Math.floor(Math.random() * zeroWidthChars.length)];
    }
    chunks[chunks.length - 1] += salt;
  }

  return chunks;
};

const KanbanPreviewUpdater = ({ setContactsPreview, setLoadingPreview }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    const fetchPreview = async () => {
      setLoadingPreview(true);
      try {
        const { data } = await api.get("/campaigns/preview-contacts", {
          params: {
            contactListId: values.contactListId || undefined,
            kanbanBoardId: values.kanbanBoardId || undefined,
            destinationKanbanBoardId: values.destinationKanbanBoardId || undefined,
            targetLaneId: values.targetLaneId || undefined,
            moveToLaneId: values.moveToLaneId || undefined,
            targetTagIds: JSON.stringify(values.targetTagIds || []),
          }
        });
        setContactsPreview(data);
      } catch (err) {
        console.error("Error fetching preview:", err);
      } finally {
        setLoadingPreview(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchPreview();
    }, 800);

    return () => clearTimeout(delayDebounceFn);
  }, [values.contactListId, values.kanbanBoardId, values.destinationKanbanBoardId, values.targetLaneId, values.targetTagIds, values.moveToLaneId]);

  return null;
};

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const isMounted = useRef(true);

  const resolveSpintaxPreview = (text) => {
    if (!text || !text.trim()) return "¡Hola! 👋 Te invitamos a conocer nuestras novedades...";
    let parsed = text;
    const spintaxRegex = /\{([^{}]+)\}/g;
    let iterations = 0;
    while (spintaxRegex.test(parsed) && iterations < 10) {
      parsed = parsed.replace(spintaxRegex, (_, optionsStr) => {
        const opts = optionsStr.split('|');
        return opts[0] ? opts[0].trim() : "";
      });
      iterations++;
    }
    return parsed.trim();
  };
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: true,
    scheduledAt: "",
    contactListId: "",
    tagListId: "",
    companyId,
    statusTicket: "pending",
    openTicket: "enabled",
    // Novos campos de recorrência
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null,
    targetLaneId: "",
    moveToLaneId: "",
    kanbanBoardId: "",
    destinationKanbanBoardId: "",
    addTags: [],
    maxContacts: 250,
    warmupTier: 4,
    cutoffHour: "18:00",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Lima",
    targetTagIds: [],
    audienceType: "contactList",
    // [ANTIGRAVITY] Integración IA
    aiGeneration: false,
    aiPrompt: ""
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  const [whatsappId, setWhatsappId] = useState(null);
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [kanbanBoards, setKanbanBoards] = useState([]);

  // [DESTINO] ¿El embudo tiene asignada esta conexión? (tabla N:M + columna legacy)
  const boardHasWhatsapp = (board, wid) =>
    (board?.whatsapps || []).some(w => String(w.id) === String(wid)) ||
    String(board?.whatsappId) === String(wid);
  const [messageTab, setMessageTab] = useState(0);
  const [attachments, setAttachments] = useState({ 1: null, 2: null, 3: null, 4: null, 5: null });
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [previewExecutions, setPreviewExecutions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [guidedOption1, setGuidedOption1] = useState("SÍ, QUIERO UNIRME");
  const [guidedOption2, setGuidedOption2] = useState("NO, GRACIAS");

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [contactsPreview, setContactsPreview] = useState({ count: 0, contacts: [] });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);

  // ── Modo Envío Con IA ─────────────────────────────────────────────────────
  const [sendMode, setSendMode] = useState("manual"); // "manual" | "ia" | "official"
  const [financeAlertOpen, setFinanceAlertOpen] = useState(false);
  const [aiProvider, setAiProvider] = useState("openai");
  const [aiModel, setAiModel] = useState("");
  const [aiApiKey, setAiApiKey] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  // const [aiModels, setAiModels] = useState([]);
  // const [aiLoadingModels, setAiLoadingModels] = useState(false);
  const [aiMessagesGenerated, setAiMessagesGenerated] = useState(false);
  const { findAll: findAllQueues } = useQueues();

  // Opções para dias da semana
  const daysOfWeekOptions = [
    { value: 0, label: i18n.t("campaigns.recurrence.title") }, // placeholder, will not use label here directly in map
    { value: 0, label: i18n.t("weekdays.sunday") || 'Domingo' },
    { value: 1, label: i18n.t("weekdays.monday") || 'Segunda-feira' },
    { value: 2, label: i18n.t("weekdays.tuesday") || 'Terça-feira' },
    { value: 3, label: i18n.t("weekdays.wednesday") || 'Quarta-feira' },
    { value: 4, label: i18n.t("weekdays.thursday") || 'Quinta-feira' },
    { value: 5, label: i18n.t("weekdays.friday") || 'Sexta-feira' },
    { value: 6, label: i18n.t("weekdays.saturday") || 'Sábado' },
  ].filter((d, i) => i !== 0); // remove placeholder

  // ── Handlers IA Campaña ──────────────────────────────────────────────────
  // handleLoadAiModels removed

  const handleGenerateAiMessages = async (setFieldValue) => {
    if (!aiPrompt.trim()) {
      toast.warn("Por favor completa el mensaje base");
      return;
    }
    setAiGenerating(true);
    setAiMessagesGenerated(false);
    try {
      const { data } = await api.post("/openai/generate-messages", {
        prompt: aiPrompt.trim()
      });
      if (data.messages && data.messages.length > 0) {
        setFieldValue("message1", data.messages[0]);
        setFieldValue("message2", data.messages[1] || "");
        setFieldValue("message3", data.messages[2] || "");
        setFieldValue("message4", data.messages[3] || "");
        setFieldValue("message5", data.messages[4] || "");
        setAiMessagesGenerated(true);
        setMessageTab(0);
        toast.success("✅ ¡Mensajes generados! Edítalos en los tabs de abajo.");
      } else {
        toast.error("La IA no devolvió mensajes. Intenta de nuevo.");
      }
    } catch (e) {
      if (e.response && e.response.status === 402) {
        toast.error("⚠️ No tienes saldo de IA suficiente. Por favor recarga en el módulo Financiero.", { autoClose: 10000 });
      } else {
        toastError(e);
      }
    } finally {
      setAiGenerating(false);
    }
  };

  // Função para preview das execuções
  const handlePreviewRecurrence = async (values) => {
    if (!values.isRecurring || !values.recurrenceType || !values.scheduledAt) {
      setPreviewExecutions([]);
      return;
    }

    try {
      const params = {
        recurrenceType: values.recurrenceType,
        recurrenceInterval: values.recurrenceInterval,
        recurrenceDaysOfWeek: JSON.stringify(values.recurrenceDaysOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth,
      };

      if (campaignId) {
        const { data } = await api.get(`/campaigns/${campaignId}/recurrence-preview`, { params });
        setPreviewExecutions(data.executions);
      } else {
        // Para campanhas novas, calcular localmente ou usar endpoint genérico
        const mockExecutions = calculateMockExecutions(values);
        setPreviewExecutions(mockExecutions);
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  // Função auxiliar para calcular execuções mock
  const calculateMockExecutions = (values) => {
    const executions = [];
    let currentDate = moment(values.scheduledAt);

    for (let i = 0; i < 5; i++) {
      executions.push(currentDate.format('DD/MM/YYYY HH:mm'));

      switch (values.recurrenceType) {
        case 'minutely':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'minutes');
          break;
        case 'hourly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'hours');
          break;
        case 'daily':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'days');
          break;
        case 'weekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'weeks');
          break;
        case 'biweekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval * 2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'months');
          if (values.recurrenceDayOfMonth) {
            currentDate = currentDate.date(values.recurrenceDayOfMonth);
          }
          break;
        case 'yearly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'years');
          break;
      }
    }

    return executions;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (sendMode === "con_ia") {
      api.get("/campaign-settings").then(({ data }) => {
        if (Array.isArray(data)) {
          const apiProv = data.find(d => d.key === "aiProvider")?.value || "openai";
          const apiKey = data.find(d => d.key === `${apiProv}ApiKey`)?.value || "";
          const model = data.find(d => d.key === `${apiProv}Model`)?.value || "";
          setAiProvider(apiProv);
          setAiApiKey(apiKey);
          setAiModel(model);
        }
      });
    }
  }, [sendMode]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api.get("/quick-messages/list", { params: { companyId, isOficial: "true" } }).then(({ data }) => setTemplates(data)).catch((err) => console.error("Error fetching templates:", err));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));
          setWhatsapps(mappedWhatsapps);
        });

      api.get(`/tags/list`, { params: { companyId } })
        .then(({ data }) => {
          const fetchedTags = data;
          const formattedTagLists = fetchedTags
            .map((tag) => {
              // Combinar IDs de etiquetas desde laneTags (JSON) y kanbanLaneTags (Relación)
              const jsonIds = Array.isArray(tag.laneTags) ? tag.laneTags : [];
              const relationIds = Array.isArray(tag.kanbanLaneTags)
                ? tag.kanbanLaneTags.map(klt => String(klt.tagId))
                : [];

              // Unificar y eliminar duplicados, asegurando que todo sean strings
              const unifiedLaneTags = [...new Set([...jsonIds.map(String), ...relationIds])];

              return {
                id: tag.id,
                name: tag.name, // [ANTIGRAVITY] Removido prefijo [KANBAN] por petición
                kanban: tag.kanban,
                tagsystem: tag.tagsystem,
                kanbanBoardId: tag.kanbanBoardId,
                laneTags: unifiedLaneTags
              };
            });
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      api.get(`/kanban-boards`, { params: { companyId } })
        .then(({ data }) => {
          setKanbanBoards(data);
        })
        .catch((error) => {
          console.error("Error retrieving kanban boards:", error);
        });

      if (!campaignId) {
        setGuidedOption1("SÍ, QUIERO UNIRME");
        setGuidedOption2("NO, GRACIAS");
        return;
      }

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        // Parsear arrays que pueden venir como JSON string desde el backend
        const parseArr = (val) => {
          if (Array.isArray(val)) return val;
          if (typeof val === 'string') { try { return JSON.parse(val); } catch { return []; } }
          return [];
        };

        let initialOpt1 = "SÍ, QUIERO UNIRME";
        let initialOpt2 = "NO, GRACIAS";
        let cleanConfMsg1 = data.confirmationMessage1 || "";
        const btnMatches = [...cleanConfMsg1.matchAll(/\[BOTON:\s*([^\]]+)\]/gi)];
        if (btnMatches.length >= 2) {
          initialOpt1 = btnMatches[0][1].trim();
          initialOpt2 = btnMatches[1][1].trim();
          cleanConfMsg1 = cleanConfMsg1.replace(/\[BOTON:\s*([^\]]+)\]/gi, '').trim();
        } else if (btnMatches.length === 1) {
          initialOpt1 = btnMatches[0][1].trim();
          cleanConfMsg1 = cleanConfMsg1.replace(/\[BOTON:\s*([^\]]+)\]/gi, '').trim();
        }
        setGuidedOption1(initialOpt1);
        setGuidedOption2(initialOpt2);

        let formattedScheduledAt = data.scheduledAt;
        if (formattedScheduledAt) {
          formattedScheduledAt = moment(formattedScheduledAt).format("YYYY-MM-DDTHH:mm");
        }
        
        let formattedRecurrenceEndDate = data.recurrenceEndDate;
        if (formattedRecurrenceEndDate) {
          formattedRecurrenceEndDate = moment(formattedRecurrenceEndDate).format("YYYY-MM-DD");
        }

        setCampaign(prev => ({
          ...prev,
          ...data,
          warmupTier: data.warmupTier || 4,
          cutoffHour: data.cutoffHour || "18:00",
          timezone: data.timezone || prev.timezone || "America/Lima",
          confirmationMessage1: cleanConfMsg1,
          scheduledAt: formattedScheduledAt || "",
          recurrenceEndDate: formattedRecurrenceEndDate || "",
          addTags: parseArr(data.addTags),
          targetTagIds: parseArr(data.targetTagIds),
          recurrenceDaysOfWeek: parseArr(data.recurrenceDaysOfWeek),
        }));
        if (data.whatsappIds) {
          try {
            setSelectedWhatsapps(JSON.parse(data.whatsappIds));
          } catch (e) {
            setSelectedWhatsapps(data.whatsappId ? [data.whatsappId] : []);
          }
        } else if (data.whatsappId) {
          setSelectedWhatsapps([data.whatsappId]);
        }
        if (data.queueId)    setSelectedQueue(data.queueId);
        if (data.userId)     setSelectedUser({ id: data.userId, name: data.user?.name || '' });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  useEffect(() => {
    const isEditable =
      campaign.status === "INATIVA" ||
      campaign.status === "PROGRAMADA" ||
      campaign.status === "CANCELADA";

    setCampaignEditable(isEditable);
  }, [campaign.status]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setPreviewExecutions([]);
    setShowPreview(false);
    // Reset estado IA
    setSendMode("sin_ia");
    setAiProvider("openai");
    setAiModel("");
    setAiApiKey("");
    setAiPrompt("");
    setAiMessagesGenerated(false);
    setAiGenerating(false);
  };

  const handleAttachmentFile = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setAttachments(prev => ({ ...prev, [index]: file }));
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      const dataValues = {
        ...values,
        whatsappId: selectedWhatsapps.length > 0 ? selectedWhatsapps[0] : null,
        whatsappIds: JSON.stringify(selectedWhatsapps),
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null,
        // CORREÇÃO: Garantir conversão correta do array
        recurrenceDaysOfWeek: (values.isRecurring && values.recurrenceDaysOfWeek && values.recurrenceDaysOfWeek.length > 0)
          ? values.recurrenceDaysOfWeek // Enviar array, o backend irá converter para JSON
          : null, // Enviar null se não for recorrente ou array vazio
        contactListId: values.contactListId || null,
        kanbanBoardId: values.kanbanBoardId || null,
        destinationKanbanBoardId: values.destinationKanbanBoardId || null,
        targetLaneId: values.targetLaneId || null,
        moveToLaneId: values.moveToLaneId || null,
        addTags: JSON.stringify(values.addTags),
        maxContacts: values.maxContacts || 250,
        warmupTier: values.warmupTier || 4,
        cutoffHour: values.cutoffHour || "18:00",
        timezone: values.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Lima",
        targetTagIds: JSON.stringify(values.targetTagIds || []),
      };

      // Processar datas
      Object.entries(values).forEach(([key, value]) => {
        if (key === "scheduledAt" && value !== "" && value !== null) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else if (key !== "recurrenceDaysOfWeek") { // Não processar recurrenceDaysOfWeek aqui
          dataValues[key] = value === "" ? null : value;
        }
      });

      // Garantir que campos não recorrentes sejam null quando isRecurring é false
      if (!values.isRecurring) {
        dataValues.recurrenceType = null;
        dataValues.recurrenceInterval = null;
        dataValues.recurrenceDaysOfWeek = null;
        dataValues.recurrenceDayOfMonth = null;
        dataValues.recurrenceEndDate = null;
        dataValues.maxExecutions = null;
      }

      // [ANTIGRAVITY] Plan 1248: Validación estricta anti-ban para campañas directas
      if (!values.confirmation && sendMode !== "official") {
        const msgs = [values.message1, values.message2, values.message3, values.message4, values.message5];
        const report = evaluate5MessagesVariation(msgs);
        if (!report.isComplete) {
          toast.error("Para campañas directas, es obligatorio redactar los 5 mensajes alternativos (mínimo 20 caracteres cada uno).");
          return;
        }
        if (report.conflicts.length > 0) {
          const first = report.conflicts[0];
          toast.error(`Los Mensajes ${first.indexA + 1} y ${first.indexB + 1} son un ${first.similarity}% idénticos. Deben tener al menos 30% de diferencia para evitar baneos.`);
          return;
        }
      }

      // [ANTIGRAVITY] Plan 1247: Empaquetar opciones guiadas en confirmationMessage1
      if (values.confirmation) {
        const opt1 = (guidedOption1 || "SÍ, QUIERO UNIRME").trim();
        const opt2 = (guidedOption2 || "NO, GRACIAS").trim();
        const cleanBody = (values.confirmationMessage1 || "")
          .replace(/\[BOTON:\s*([^\]]+)\]/gi, '')
          .trim();
        dataValues.confirmationMessage1 = `${cleanBody}\n\n[BOTON: ${opt1}] [BOTON: ${opt2}]`;
      }

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);
        for (let i = 1; i <= 5; i++) {
          if (attachments[i] != null) {
            const formData = new FormData();
            formData.append("file", attachments[i]);
            await api.post(`/campaigns/${campaignId}/media-upload?index=${i}`, formData);
          }
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);
        for (let i = 1; i <= 5; i++) {
          if (attachments[i] != null) {
            const formData = new FormData();
            formData.append("file", attachments[i]);
            await api.post(`/campaigns/${data.id}/media-upload?index=${i}`, formData);
          }
        }
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async (index) => {
    if (attachments[index]) {
      setAttachments(prev => ({ ...prev, [index]: null }));
    }

    if (campaign[`mediaPath${index}`]) {
      await api.delete(`/campaigns/${campaign.id}/media-upload?index=${index}`);
      setCampaign((prev) => ({ ...prev, [`mediaPath${index}`]: null, [`mediaName${index}`]: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const renderMessageField = (identifier, customLabel = null) => {
    const index = parseInt(identifier.replace('message', ''), 10);
    const mediaNameKey = `mediaName${index}`;
    const mediaPathKey = `mediaPath${index}`;
    const fileInputId = `file-input-${index}`;

    return (
      <Field name={identifier}>
        {({ field, form }) => {
          const messageValue = field.value || "";
          const charCount = messageValue.length;
          const hasMedia = attachments[index] != null || campaign[mediaPathKey] != null;
          const isWarning = hasMedia && charCount > 700;
          
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <TextField
                {...field}
                id={identifier}
                fullWidth
                minRows={5}
                label={customLabel || i18n.t(`campaigns.dialog.form.${identifier}`)}
                placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
                multiline={true}
                variant="outlined"
                helperText={
                  isWarning 
                    ? <span style={{color: '#ff9800', fontWeight: 'bold'}}>{i18n.t("campaigns.dialog.charCountWarning", { count: charCount })}</span>
                    : <span>{i18n.t("campaigns.dialog.charCount", { count: charCount })} {i18n.t("campaigns.dialog.help", { name: "{{name}}", contact_number: "{{contact_number}}", agent_email: "{{agent_email}}", interpolation: { escapeValue: false } })}</span>
                }
                disabled={!campaignEditable && campaign.status !== "CANCELADA"}
                inputProps={{ style: { resize: 'vertical' } }}
                InputProps={{ style: { resize: 'vertical' } }}
              />
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="file"
                  id={fileInputId}
                  style={{ display: 'none' }}
                  onChange={(e) => handleAttachmentFile(e, index)}
                />
                
                {hasMedia ? (
                  <>
                    <Button startIcon={<AttachFileIcon />} style={{ textTransform: 'none' }}>
                      {attachments[index] ? attachments[index].name : campaign[mediaNameKey]}
                    </Button>
                    {campaignEditable && (
                      <IconButton onClick={() => deleteMedia(index)} color="secondary" size="small">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                ) : (
                  campaignEditable && (
                    <Button
                      color="primary"
                      onClick={() => document.getElementById(fileInputId).click()}
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFileIcon />}
                    >
                      {i18n.t('campaigns.dialog.buttons.attach')}
                    </Button>
                  )
                )}
              </div>
            </div>
          );
        }}
      </Field>
    );
  };

  const renderConfirmationMessageField = (identifier, customLabel = null) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        minRows={5}
        label={customLabel || i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
        inputProps={{ style: { resize: 'vertical' } }}
        InputProps={{ style: { resize: 'vertical' } }}
      />
    );
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper, style: { minWidth: '85vw', maxHeight: '95vh' } }}
      >
        <DialogTitle id="form-dialog-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {campaignEditable ? (
              <>
                {campaignId
                  ? `${i18n.t("campaigns.dialog.update")}`
                  : `${i18n.t("campaigns.dialog.new")}`}
              </>
            ) : (
              <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
            )}
          </div>
          <PageHelpGuide helpData={CAMPAIGNS_HELP_DATA} />
        </DialogTitle>

        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={getCampaignSchema(sendMode)}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue, validateForm }) => (
            <Form className={classes.formContainer}>
              <KanbanPreviewUpdater setContactsPreview={setContactsPreview} setLoadingPreview={setLoadingPreview} />
              <DialogContent dividers className={classes.dialogContentScroll}>
                <Grid spacing={2} container>
                  <Grid xs={12} md={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="confirmation-selection-label">
                        {i18n.t("campaigns.dialog.form.confirmation")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.confirmation")}
                        placeholder={i18n.t("campaigns.dialog.form.confirmation")}
                        labelId="confirmation-selection-label"
                        id="confirmation"
                        name="confirmation"
                        error={touched.confirmation && Boolean(errors.confirmation)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={false}>{i18n.t("campaigns.table.disabled")}</MenuItem>
                        <MenuItem value={true}>{i18n.t("campaigns.table.enabled")}</MenuItem>
                      </Field>
                      <FormHelperText style={{ color: values.confirmation ? '#10b981' : '#f59e0b', fontWeight: 'bold' }}>
                        {values.confirmation ? i18n.t("campaigns.dialog.form.confirmationHelperActive") : i18n.t("campaigns.dialog.form.confirmationHelperInactive")}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  {/* [ANTIGRAVITY] IA Generation Switch - Now 3 Tabs */}
                  <Grid xs={12} item>

                    <StandardTabs
                      fullWidth
                      value={sendMode === "manual" || sendMode === "ia" ? 0 : 1}
                      onChange={(e, newValue) => {
                        const newMode = newValue === 0 ? "manual" : "official";
                        setSendMode(newMode);
                        setFieldValue("aiGeneration", false);
                      }}
                      items={[
                        {
                          label: i18n.t("campaigns.dialog.form.standardConnectionTab"),
                          value: 0,
                          icon: <WhatsAppIcon fontSize="small" />,
                          disabled: !campaignEditable,
                        },
                        {
                          label: i18n.t("campaigns.dialog.form.officialApiTab"),
                          value: 1,
                          icon: <CloudQueueIcon fontSize="small" />,
                          disabled: !campaignEditable,
                        },
                      ]}
                      style={{ marginBottom: 15 }}
                    />
                    
                    
                    {sendMode === "official" && (
                      <Alert severity="warning" icon={<span>🛡️</span>} style={{ marginBottom: 16 }}>
                        <AlertTitle>{i18n.t("campaigns.dialog.form.officialApiAlertTitle")}</AlertTitle>
                        {i18n.t("campaigns.dialog.form.officialApiAlertDesc")}
                      </Alert>
                    )}

                  </Grid>
                  <Grid xs={12} item>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '10px', marginTop: '10px' }}>
                      {i18n.t("campaigns.dialog.form.section1")}
                    </Typography>
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel
                        id="whatsapp-selection-label"
                        shrink={selectedWhatsapps.length > 0 ? true : undefined}
                      >
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Select
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        labelId="whatsapp-selection-label"
                        id="whatsappIds"
                        required
                        error={touched.whatsappId && Boolean(errors.whatsappId) && selectedWhatsapps.length === 0}
                        disabled={!campaignEditable && !!campaignId}
                        value={selectedWhatsapps.length > 0 ? String(selectedWhatsapps[0]) : ""}
                        onChange={(event) => {
                          setSelectedWhatsapps([event.target.value]);
                        }}
                      >
                        <MenuItem value="">
                          <em>{i18n.t("campaigns.dialog.form.selectConnection") || "Seleccione una conexión"}</em>
                        </MenuItem>
                        {whatsapps
                          .filter(w => w.status === "CONNECTED" && (sendMode === "official" ? (w.channel === "whatsapp_oficial" || w.channel === "whatsapp_meta") : w.channel === "whatsapp"))
                          .filter(w => {
                            // [DESTINO] Si se eligió un embudo destino, solo conexiones de ese embudo
                            if (values.audienceType === 'kanban' || !values.destinationKanbanBoardId) return true;
                            const board = kanbanBoards.find(kb => String(kb.id) === String(values.destinationKanbanBoardId));
                            return !board || boardHasWhatsapp(board, w.id);
                          })
                          .map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={String(whatsapp.id)}>
                              {whatsapp.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      name="scheduledAt"
                      error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                      helperText={touched.scheduledAt && errors.scheduledAt}
                      variant="outlined"
                      margin="dense"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  {/* [ANTIGRAVITY] Plan 1249: Sistema de Goteo Diario Inteligente y Tiers de Calentamiento */}
                  <Grid xs={12} item>
                    <Card variant="outlined" style={{ padding: 16, backgroundColor: isDark ? '#1e293b' : '#f8fafc', borderRadius: 8, marginTop: 4, marginBottom: 8, border: isDark ? '1px solid #334155' : '1px solid #cbd5e1' }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
                        <Box display="flex" alignItems="center">
                          <SpeedIcon style={{ marginRight: 8, color: '#3b82f6' }} />
                          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                            {i18n.t("campaigns.dialog.form.warmupTierTitle")}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Chip
                            icon={<AccessTimeIcon style={{ fontSize: 16 }} />}
                            label={`${i18n.t("campaigns.dialog.form.timezoneLabel")}: ${values.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Lima"}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                          <Chip
                            icon={<ScheduleIcon style={{ fontSize: 16 }} />}
                            label={`${i18n.t("campaigns.dialog.form.cutoffWindowLabel")}: 09:00 - ${values.cutoffHour || "18:00"}`}
                            size="small"
                            style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                        {i18n.t("campaigns.dialog.form.warmupTierDescription")}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card
                            onClick={() => {
                              if (campaignEditable) {
                                setFieldValue("warmupTier", 1);
                                setFieldValue("maxContacts", 30);
                              }
                            }}
                            style={{
                              cursor: campaignEditable ? 'pointer' : 'default',
                              padding: 12,
                              borderRadius: 8,
                              border: values.warmupTier === 1 ? '2px solid #10b981' : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
                              backgroundColor: values.warmupTier === 1 ? (isDark ? '#064e3b' : '#ecfdf5') : 'transparent',
                              textAlign: 'center',
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: values.warmupTier === 1 ? '#059669' : 'inherit' }}>
                              {i18n.t("campaigns.dialog.form.tier1Title")}
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 800, margin: '4px 0' }}>
                              30 <span style={{ fontSize: 12, fontWeight: 400 }}>{i18n.t("campaigns.dialog.form.perDay")}</span>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {i18n.t("campaigns.dialog.form.tier1Desc")}
                            </Typography>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card
                            onClick={() => {
                              if (campaignEditable) {
                                setFieldValue("warmupTier", 2);
                                setFieldValue("maxContacts", 75);
                              }
                            }}
                            style={{
                              cursor: campaignEditable ? 'pointer' : 'default',
                              padding: 12,
                              borderRadius: 8,
                              border: values.warmupTier === 2 ? '2px solid #3b82f6' : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
                              backgroundColor: values.warmupTier === 2 ? (isDark ? '#1e3a8a' : '#eff6ff') : 'transparent',
                              textAlign: 'center',
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: values.warmupTier === 2 ? '#2563eb' : 'inherit' }}>
                              {i18n.t("campaigns.dialog.form.tier2Title")}
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 800, margin: '4px 0' }}>
                              75 <span style={{ fontSize: 12, fontWeight: 400 }}>{i18n.t("campaigns.dialog.form.perDay")}</span>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {i18n.t("campaigns.dialog.form.tier2Desc")}
                            </Typography>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card
                            onClick={() => {
                              if (campaignEditable) {
                                setFieldValue("warmupTier", 3);
                                setFieldValue("maxContacts", 150);
                              }
                            }}
                            style={{
                              cursor: campaignEditable ? 'pointer' : 'default',
                              padding: 12,
                              borderRadius: 8,
                              border: values.warmupTier === 3 ? '2px solid #8b5cf6' : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
                              backgroundColor: values.warmupTier === 3 ? (isDark ? '#4c1d95' : '#f5f3ff') : 'transparent',
                              textAlign: 'center',
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: values.warmupTier === 3 ? '#7c3aed' : 'inherit' }}>
                              {i18n.t("campaigns.dialog.form.tier3Title")}
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 800, margin: '4px 0' }}>
                              150 <span style={{ fontSize: 12, fontWeight: 400 }}>{i18n.t("campaigns.dialog.form.perDay")}</span>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {i18n.t("campaigns.dialog.form.tier3Desc")}
                            </Typography>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card
                            onClick={() => {
                              if (campaignEditable) {
                                setFieldValue("warmupTier", 4);
                                setFieldValue("maxContacts", 250);
                              }
                            }}
                            style={{
                              cursor: campaignEditable ? 'pointer' : 'default',
                              padding: 12,
                              borderRadius: 8,
                              border: values.warmupTier === 4 ? '2px solid #f59e0b' : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
                              backgroundColor: values.warmupTier === 4 ? (isDark ? '#78350f' : '#fffbeb') : 'transparent',
                              textAlign: 'center',
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: values.warmupTier === 4 ? '#d97706' : 'inherit' }}>
                              {i18n.t("campaigns.dialog.form.tier4Title")}
                            </Typography>
                            <Typography variant="h6" style={{ fontWeight: 800, margin: '4px 0' }}>
                              250 <span style={{ fontSize: 12, fontWeight: 400 }}>{i18n.t("campaigns.dialog.form.perDay")}</span>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {i18n.t("campaigns.dialog.form.tier4Desc")}
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Proyección dinámica de entrega */}
                      <Box mt={2} p={1.5} borderRadius={6} style={{ backgroundColor: isDark ? '#0f172a' : '#f1f5f9', borderLeft: '4px solid #3b82f6' }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: isDark ? '#93c5fd' : '#1e40af' }}>
                          📅 {i18n.t("campaigns.dialog.form.deliveryProjectionTitle")}
                        </Typography>
                        <Typography variant="body2" style={{ marginTop: 4 }}>
                          {i18n.t("campaigns.dialog.form.deliveryProjectionDesc", {
                            total: contactsPreview.count,
                            daily: values.maxContacts || 250,
                            days: Math.max(1, Math.ceil((contactsPreview.count || 1) / (values.maxContacts || 250))),
                            cutoff: values.cutoffHour || "18:00"
                          })}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* SEÇÃO DE RECORRÊNCIA */}
                  <Grid xs={12} item>
                    <Card className={classes.recurrenceCard}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <RepeatIcon className={classes.recurrenceIcon} />
                          <Typography variant="h6">
                            {i18n.t("campaigns.recurrence.title")}
                          </Typography>
                        </Box>

                        <Grid spacing={2} container>
                          <Grid xs={12} item>
                            <FormControlLabel
                              control={
                                <Field
                                  as={Switch}
                                  name="isRecurring"
                                  checked={values.isRecurring}
                                  onChange={(e) => {
                                    setFieldValue('isRecurring', e.target.checked);
                                    if (!e.target.checked) {
                                      setPreviewExecutions([]);
                                      setShowPreview(false);
                                    }
                                  }}
                                  disabled={!campaignEditable}
                                />
                              }
                              label={i18n.t("campaigns.recurrence.enable")}
                            />
                          </Grid>

                          <Collapse in={values.isRecurring}>
                            <Grid spacing={2} container>
                              <Grid xs={12} md={3} item>
                                <FormControl
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  error={touched.recurrenceType && Boolean(errors.recurrenceType)}
                                >
                                  <InputLabel>{i18n.t("campaigns.recurrence.type")}</InputLabel>
                                  <Field
                                    as={Select}
                                    name="recurrenceType"
                                    label={i18n.t("campaigns.recurrence.type")}
                                    disabled={!campaignEditable}
                                    onChange={(e) => {
                                      setFieldValue('recurrenceType', e.target.value);
                                      setFieldValue('recurrenceDaysOfWeek', []);
                                      setFieldValue('recurrenceDayOfMonth', 1);
                                    }}
                                  >
                                    <MenuItem value="minutely">{i18n.t("campaigns.recurrence.minutely")}</MenuItem>
                                    <MenuItem value="hourly">{i18n.t("campaigns.recurrence.hourly")}</MenuItem>
                                    <MenuItem value="daily">{i18n.t("campaigns.recurrence.daily")}</MenuItem>
                                    <MenuItem value="weekly">{i18n.t("campaigns.recurrence.weekly")}</MenuItem>
                                    <MenuItem value="biweekly">{i18n.t("campaigns.recurrence.biweekly")}</MenuItem>
                                    <MenuItem value="monthly">{i18n.t("campaigns.recurrence.monthly")}</MenuItem>
                                    <MenuItem value="yearly">{i18n.t("campaigns.recurrence.yearly")}</MenuItem>
                                  </Field>
                                  {touched.recurrenceType && errors.recurrenceType && (
                                    <FormHelperText error>{errors.recurrenceType}</FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>

                              <Grid xs={12} md={3} item>
                                <Field
                                  as={TextField}
                                  name="recurrenceInterval"
                                  label={i18n.t("campaigns.recurrence.interval")}
                                  type="number"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                  error={touched.recurrenceInterval && Boolean(errors.recurrenceInterval)}
                                  helperText={
                                    touched.recurrenceInterval && errors.recurrenceInterval ||
                                    `${i18n.t("campaigns.recurrence.every")} ${values.recurrenceInterval || 1} ${values.recurrenceType === 'minutely' ? i18n.t("campaigns.recurrence.unit_minute") :
                                      values.recurrenceType === 'hourly' ? i18n.t("campaigns.recurrence.unit_hour") :
                                        values.recurrenceType === 'daily' ? i18n.t("campaigns.recurrence.unit_day") :
                                          values.recurrenceType === 'weekly' ? i18n.t("campaigns.recurrence.unit_week") :
                                            values.recurrenceType === 'biweekly' ? i18n.t("campaigns.recurrence.unit_biweek") :
                                              values.recurrenceType === 'monthly' ? i18n.t("campaigns.recurrence.unit_month") :
                                                values.recurrenceType === 'yearly' ? i18n.t("campaigns.recurrence.unit_year") : ''
                                    }`
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              {values.recurrenceType === 'weekly' && (
                                <Grid xs={12} md={6} item>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {i18n.t("campaigns.recurrence.weekDays")}
                                  </Typography>
                                  <FormGroup row>
                                    {daysOfWeekOptions.map((day) => (
                                      <FormControlLabel
                                        key={day.value}
                                        control={
                                          <Checkbox
                                            checked={values.recurrenceDaysOfWeek.includes(day.value)}
                                            onChange={(e) => {
                                              const currentDays = values.recurrenceDaysOfWeek || [];
                                              if (e.target.checked) {
                                                setFieldValue('recurrenceDaysOfWeek', [...currentDays, day.value]);
                                              } else {
                                                setFieldValue('recurrenceDaysOfWeek',
                                                  currentDays.filter(d => d !== day.value)
                                                );
                                              }
                                            }}
                                            disabled={!campaignEditable}
                                          />
                                        }
                                        label={day.label.substring(0, 3)}
                                      />
                                    ))}
                                  </FormGroup>
                                  {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && (
                                    <FormHelperText error>{errors.recurrenceDaysOfWeek}</FormHelperText>
                                  )}
                                </Grid>
                              )}

                              {values.recurrenceType === 'monthly' && (
                                <Grid xs={12} md={3} item>
                                  <Field
                                    as={TextField}
                                    name="recurrenceDayOfMonth"
                                    label={i18n.t("campaigns.recurrence.monthDay")}
                                    type="number"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    inputProps={{ min: 1, max: 31 }}
                                    error={touched.recurrenceDayOfMonth && Boolean(errors.recurrenceDayOfMonth)}
                                    helperText={
                                      touched.recurrenceDayOfMonth && errors.recurrenceDayOfMonth ||
                                      i18n.t("campaigns.recurrence.monthDayHelp")
                                    }
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}

                              <Grid xs={12} md={6} item>
                                <Field
                                  as={TextField}
                                  name="recurrenceEndDate"
                                  label={i18n.t("campaigns.recurrence.endDate")}
                                  type="date"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  error={touched.recurrenceEndDate && Boolean(errors.recurrenceEndDate)}
                                  helperText={
                                    touched.recurrenceEndDate && errors.recurrenceEndDate ||
                                    i18n.t("campaigns.recurrence.infiniteHelp")
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              <Grid xs={12} md={6} item>
                                <Field
                                  as={TextField}
                                  name="maxExecutions"
                                  label={i18n.t("campaigns.recurrence.maxExecutions")}
                                  type="number"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                  error={touched.maxExecutions && Boolean(errors.maxExecutions)}
                                  helperText={
                                    touched.maxExecutions && errors.maxExecutions ||
                                    i18n.t("campaigns.recurrence.infiniteHelp")
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              <Grid xs={12} md={6} item>
                                <Button
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => {
                                    handlePreviewRecurrence(values);
                                    setShowPreview(!showPreview);
                                  }}
                                  disabled={!values.recurrenceType || !values.scheduledAt}
                                  fullWidth
                                  style={{ marginTop: 8 }}
                                >
                                  {showPreview ? i18n.t("campaigns.recurrence.hidePreview") : i18n.t("campaigns.recurrence.showPreview")}
                                </Button>
                              </Grid>

                              {/* Preview das execuções */}
                              <Collapse in={showPreview && previewExecutions.length > 0}>
                                <Grid xs={12} item>
                                  <Card className={classes.previewCard}>
                                    <CardContent>
                                      <Typography variant="subtitle2" gutterBottom>
                                        {i18n.t("campaigns.recurrence.nextExecutions")}
                                      </Typography>
                                      <List dense>
                                        {previewExecutions.slice(0, 5).map((execution, index) => (
                                          <ListItem key={index} divider>
                                            <ListItemText
                                              primary={i18n.t("campaigns.recurrence.executionLabel", { number: index + 1 })}
                                              secondary={typeof execution === 'string' ? execution : moment(execution).format('DD/MM/YYYY HH:mm')}
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Collapse>
                            </Grid>
                          </Collapse>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid xs={12} item>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '10px', marginTop: '10px' }}>
                      {i18n.t("campaigns.dialog.form.section2")}
                    </Typography>
                  </Grid>

                  <Grid xs={12} item>
                    <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                      <InputLabel id="audienceType-label">{i18n.t("campaigns.dialog.form.audienceType") || "Tipo de Público"}</InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.audienceType") || "Tipo de Público"}
                        labelId="audienceType-label"
                        name="audienceType"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="contactList">{i18n.t("campaigns.dialog.form.audienceContactList")}</MenuItem>
                        <MenuItem value="tags">{i18n.t("campaigns.dialog.form.audienceTags")}</MenuItem>
                        <MenuItem value="kanban">Por {i18n.t("campaigns.dialog.form.kanbanSegmentation") || "Segmentación Kanban"}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {values.audienceType === 'contactList' && (
                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="contactList-selection-label">
                        {i18n.t("campaigns.dialog.form.contactList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.contactList")}
                        placeholder={i18n.t("campaigns.dialog.form.contactList")}
                        labelId="contactList-selection-label"
                        id="contactListId"
                        name="contactListId"
                        error={touched.contactListId && Boolean(errors.contactListId)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">{i18n.t("campaigns.table.option")}</MenuItem>
                        {contactLists &&
                          contactLists.map((contactList) => (
                            <MenuItem key={contactList.id} value={contactList.id}>
                              {contactList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  )}

                  {values.audienceType === 'tags' && (
                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="tagList-selection-label">
                        {i18n.t("campaigns.dialog.form.tagList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.tagList")}
                        placeholder={i18n.t("campaigns.dialog.form.tagList")}
                        labelId="tagList-selection-label"
                        id="tagListId"
                        name="tagListId"
                        error={touched.tagListId && Boolean(errors.tagListId)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">{i18n.t("campaigns.table.option")}</MenuItem>
                        {tagLists &&
                          tagLists.filter(t => t.tagsystem === 1 || t.tagsystem === true).map((tagList) => (
                            <MenuItem key={tagList.id} value={tagList.id}>
                              {tagList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  )}

                  {values.audienceType === 'kanban' && selectedWhatsapps.length === 0 && (
                    <Grid xs={12} item>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: '#fff3cd', border: '1px solid #ffc107',
                        borderRadius: 8, padding: '10px 16px', marginTop: 8
                      }}>
                        <span style={{ fontSize: 20 }}>&#9888;&#65039;</span>
                        <span style={{ color: '#856404', fontWeight: 500, fontSize: 14 }}>
                          Primero selecciona una conexion de WhatsApp (Seccion 2) para poder filtrar por Kanban.
                        </span>
                      </div>
                    </Grid>
                  )}
                  {values.audienceType === 'kanban' && selectedWhatsapps.length > 0 && (
                    <Grid xs={12} item>
                      <Grid spacing={2} container>
                        <Grid xs={12} md={6} item>
                          <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                            <InputLabel id="kanbanBoardId-label">{i18n.t("campaigns.dialog.form.sourceFunnel") || "Embudo (Origen)"}</InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("campaigns.dialog.form.sourceFunnel") || "Embudo (Origen)"}
                              labelId="kanbanBoardId-label"
                              name="kanbanBoardId"
                              disabled={!campaignEditable}
                              onChange={(e) => {
                                setFieldValue("kanbanBoardId", e.target.value);
                                setFieldValue("targetLaneId", "");
                                setFieldValue("destinationKanbanBoardId", e.target.value);
                              }}
                            >
                              <MenuItem value="">{i18n.t("campaigns.dialog.form.selectFunnel") || "Seleccione un Embudo"}</MenuItem>
                              {kanbanBoards.map((kanban) => (
                                <MenuItem key={kanban.id} value={kanban.id}>
                                  {kanban.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </FormControl>
                        </Grid>
                        
                        <Grid xs={12} md={3} item>
                          <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                            <InputLabel id="targetLaneId-label">{i18n.t("campaigns.dialog.form.sourceStage") || "Etapa (Origen)"}</InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("campaigns.dialog.form.sourceStage") || "Etapa (Origen)"}
                              labelId="targetLaneId-label"
                              name="targetLaneId"
                              disabled={!campaignEditable || !values.kanbanBoardId}
                            >
                              <MenuItem value="">{i18n.t("campaigns.dialog.form.selectStage") || "Seleccione una etapa"}</MenuItem>
                              {values.kanbanBoardId
                                ? (kanbanBoards.find(kb => String(kb.id) === String(values.kanbanBoardId))?.tags || []).map((lane) => (
                                  <MenuItem key={lane.id} value={lane.id}>
                                    {lane.name}
                                  </MenuItem>
                                ))
                                : null
                              }
                            </Field>
                          </FormControl>
                        </Grid>
                        
                        <Grid xs={12} md={6} item>
                          <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                            <InputLabel shrink id="targetTagIds-label">{i18n.t("campaigns.dialog.form.sourceTags") || "Etiqueta Kanban (Origen)"}</InputLabel>
                            <Select
                              multiple
                              displayEmpty
                              input={<OutlinedInput label={i18n.t("campaigns.dialog.form.sourceTags") || "Etiqueta Kanban (Origen)"} notched />}
                              label={i18n.t("campaigns.dialog.form.sourceTags") || "Etiqueta Kanban (Origen)"}
                              labelId="targetTagIds-label"
                              name="targetTagIds"
                              disabled={!campaignEditable}
                              value={Array.isArray(values.targetTagIds) ? values.targetTagIds.map(String) : []}
                              onChange={(e) => {
                                const val = e.target.value;
                                setFieldValue("targetTagIds", typeof val === 'string' ? val.split(',') : val);
                              }}
                              renderValue={(selected) => {
                                const selectedLane = values.kanbanBoardId && values.targetLaneId
                                  ? kanbanBoards.find(kb => String(kb.id) === String(values.kanbanBoardId))
                                      ?.tags?.find(lane => String(lane.id) === String(values.targetLaneId))
                                  : null;
                                const availableTags = selectedLane?.kanbanLaneTags
                                  ? selectedLane.kanbanLaneTags.map(klt => klt.tag)
                                  : [];
                                  
                                if (!selected || selected.length === 0) {
                                  return i18n.t("campaigns.dialog.form.selectTags") || "Seleccione etiqueta(s)";
                                }
                                return availableTags
                                  .filter(t => selected.includes(String(t.id)))
                                  .map(t => t.name)
                                  .join(", ");
                              }}
                            >
                              {(values.kanbanBoardId && values.targetLaneId
                                ? (kanbanBoards.find(kb => String(kb.id) === String(values.kanbanBoardId))
                                    ?.tags?.find(lane => String(lane.id) === String(values.targetLaneId))
                                    ?.kanbanLaneTags?.map(klt => klt.tag) || [])
                                : []
                              ).map((tag) => (
                                <MenuItem key={tag.id} value={String(tag.id)}>
                                  {tag.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid xs={12} md={1} item style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Tooltip title="Total de tickets en este filtro">
                            <div style={{
                              width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#00e5ff', 
                              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', 
                              fontWeight: 'bold', fontSize: '16px', boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
                              marginTop: '8px'
                            }}>
                              {loadingPreview ? "..." : contactsPreview.count}
                            </div>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  <Grid xs={12} item>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '10px', marginTop: '10px' }}>
                      {i18n.t("campaigns.dialog.form.section3")}
                    </Typography>
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="openTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.openTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.openTicket")}
                        placeholder={i18n.t("campaigns.dialog.form.openTicket")}
                        labelId="openTicket-selection-label"
                        id="openTicket"
                        name="openTicket"
                        error={touched.openTicket && Boolean(errors.openTicket)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
                        <MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* SELECIONAR USUARIO */}
                  <Grid xs={12} md={6} item>
                    <Autocomplete
                      style={{ marginTop: '8px' }}
                      variant="outlined"
                      margin="dense"
                      className={classes.formControl}
                      getOptionLabel={(option) => `${option.name}`}
                      value={selectedUser}
                      size="small"
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue != null && Array.isArray(newValue.queues)) {
                          if (newValue.queues.length === 1) {
                            setSelectedQueue(newValue.queues[0].id);
                          }
                          setQueues(newValue.queues);
                        } else {
                          setQueues(allQueues);
                          setSelectedQueue("");
                        }
                      }}
                      options={options}
                      filterOptions={filterOptions}
                      freeSolo
                      fullWidth
                      autoHighlight
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={(e) => setSearchParam(e.target.value)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel>
                        {i18n.t("transferTicketModal.fieldQueueLabel")}
                      </InputLabel>
                      <Select
                        value={selectedQueue}
                        onChange={(e) => setSelectedQueue(e.target.value)}
                        label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                        required={!isNil(selectedUser)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                      >
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="statusTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.statusTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.statusTicket")}
                        placeholder={i18n.t("campaigns.dialog.form.statusTicket")}
                        labelId="statusTicket-selection-label"
                        id="statusTicket"
                        name="statusTicket"
                        error={touched.statusTicket && Boolean(errors.statusTicket)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                        <MenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                        <MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* SECCIÓN DE AUTOMATIZACIÓN KANBAN [DESTINO] */}
                  {(
<Grid xs={12} item>
                    <Card className={classes.recurrenceCard}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Typography variant="h6" color="primary" style={{ fontWeight: 'bold' }}>
                            {i18n.t("campaigns.dialog.form.section4") || "Sección 4: Automatización Kanban (Destino)"}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                          {values.audienceType === 'kanban' ? "Los contactos que reciban esta campaña serán actualizados dentro del Embudo Kanban." : (i18n.t("campaigns.dialog.form.destOptionalHint") || "Opcional: clasifica a los contactos en un embudo, etapa y etiqueta. Si no eliges embudo, solo quedan con Marca y Conexión.")}
                        </Typography>

                        <Grid spacing={3} container>
                          {/* SECCIÓN DESTINO */}
                          <Grid xs={12} md={6} item>
                            <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                              <InputLabel id="destinationKanbanBoardId-label">{i18n.t("campaigns.dialog.form.destFunnel") || "Embudo Destino"}</InputLabel>
                              <Field
                                as={Select}
                                name="destinationKanbanBoardId"
                                label={i18n.t("campaigns.dialog.form.destFunnel") || "Embudo Destino"}
                                labelId="destinationKanbanBoardId-label"
                                disabled={values.audienceType === 'kanban' || !campaignEditable}
                                value={values.audienceType === 'kanban' ? (values.kanbanBoardId || "") : (values.destinationKanbanBoardId || "")}
                                onChange={(e) => {
                                  const newBoardId = e.target.value;
                                  setFieldValue("destinationKanbanBoardId", newBoardId);
                                  setFieldValue("moveToLaneId", "");
                                  setFieldValue("kanbanTagId", "");
                                  // Si la conexión elegida no pertenece al embudo, se limpia
                                  const nb = kanbanBoards.find(kb => String(kb.id) === String(newBoardId));
                                  if (nb && selectedWhatsapps.length > 0 && !boardHasWhatsapp(nb, selectedWhatsapps[0])) {
                                    setSelectedWhatsapps([]);
                                    toast.warn(i18n.t("campaigns.dialog.form.destConnectionCleared") || "La conexión elegida no pertenece a ese Embudo. Selecciona una conexión del Embudo.");
                                  }
                                }}
                              >
                                {values.audienceType !== 'kanban' && (
                                  <MenuItem value=""><em>{i18n.t("campaigns.dialog.form.noFunnel") || "Sin Embudo"}</em></MenuItem>
                                )}
                                {kanbanBoards
                                  .filter(kb => values.audienceType === 'kanban' || selectedWhatsapps.length === 0 || boardHasWhatsapp(kb, selectedWhatsapps[0]))
                                  .map((kanban) => (
                                  <MenuItem key={kanban.id} value={kanban.id}>
                                    {kanban.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          </Grid>
                          
                          <Grid xs={12} md={6} item>
                            <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                              <InputLabel id="moveToLaneId-label">{i18n.t("campaigns.dialog.form.destStage") || "Mover a la Etapa"}</InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("campaigns.dialog.form.destStage") || "Mover a la Etapa"}
                                labelId="moveToLaneId-label"
                                name="moveToLaneId"
                                disabled={!campaignEditable || !(values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId)}
                              >
                                <MenuItem value="">{i18n.t("campaigns.kanbanSegment.noMove") || "Sin cambios (No mover)"}</MenuItem>
                                {(values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId) ? (kanbanBoards.find(kb => String(kb.id) === String((values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId)))?.tags || []) .filter(lane => values.audienceType !== 'kanban' || String(lane.id) !== String(values.targetLaneId))
                                      .map((lane) => (
                                    <MenuItem key={lane.id} value={lane.id}>
                                      {lane.name}
                                    </MenuItem>
                                  ))
                                  : null
                                }
                              </Field>
                            </FormControl>
                          </Grid>

                          <Grid xs={12} md={6} item>
                            <FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
                              <InputLabel id="kanbanTagId-label">{i18n.t("campaigns.kanbanSegment.addTags") || "Agregar Etiqueta"}</InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("campaigns.kanbanSegment.addTags") || "Agregar Etiqueta"}
                                labelId="kanbanTagId-label"
                                name="kanbanTagId"
                                disabled={!campaignEditable || !(values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId)}
                              >
                                <MenuItem value="">{i18n.t("campaigns.kanbanSegment.noTags") || "Sin cambios (No agregar etiqueta)"}</MenuItem>
                                {(values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId) && values.moveToLaneId ? (kanbanBoards.find(kb => String(kb.id) === String((values.audienceType === 'kanban' ? values.kanbanBoardId : values.destinationKanbanBoardId)))
                                      ?.tags?.find(lane => String(lane.id) === String(values.moveToLaneId))
                                      ?.kanbanLaneTags?.map(klt => klt.tag) || []).map((tag) => (
                                    <MenuItem key={tag.id} value={tag.id}>
                                      {tag.name}
                                    </MenuItem>
                                  ))
                                  : null
                                }
                              </Field>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  )}


                  {/* ── MODO DE ENVÍO: Movido a las pestañas superiores ─────────────────── */}
                  <Grid xs={12} item>

                    {/* ── Panel configuración IA ────────────────────────── */}
                    {sendMode === "ia" && (
                      <Card style={{ marginBottom: 16, border: "1px solid #ddd6fe", borderRadius: 10 }}>
                        <CardContent style={{ paddingBottom: 12 }}>
                          <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#7c3aed", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                            {i18n.t("campaigns.kanbanSegment.aiTitle")}
                          </Typography>
                          <Grid container spacing={2}>
                            {/* Solo mostramos el Mensaje base, la API se carga de configuración */}
                            {/* Mensaje base */}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth multiline rows={3}
                                label={i18n.t("campaigns.kanbanSegment.aiBaseMsg")}
                                variant="outlined"
                                disabled={!campaignEditable}
                                value={aiPrompt}
                                onChange={e => setAiPrompt(e.target.value)}
                                placeholder={i18n.t("campaigns.kanbanSegment.aiPlaceholder")}
                                inputProps={{ style: { resize: 'vertical' } }}
                              />
                            </Grid>
                            {/* Botones acción */}
                            <Grid item xs={12}>
                              <Box display="flex" alignItems="center" style={{ gap: 10, flexWrap: "wrap" }}>
                                <Button
                                  variant="contained"
                                  style={{
                                    background: aiGenerating ? "#9ca3af" : "#7c3aed",
                                    color: "white", borderRadius: 20, paddingLeft: 20, paddingRight: 20
                                  }}
                                  disabled={aiGenerating || !campaignEditable}
                                  onClick={() => handleGenerateAiMessages(setFieldValue)}
                                >
                                  {aiGenerating
                                    ? <><CircularProgress size={14} style={{ color: "white", marginRight: 6 }} />{i18n.t("campaigns.kanbanSegment.aiProcessing", { provider: aiProvider === 'openai' ? 'OpenAI' : aiProvider === 'gemini' ? 'Gemini' : 'DeepSeek' })}</>
                                    : i18n.t("campaigns.kanbanSegment.aiProcess")}
                                </Button>
                                {aiMessagesGenerated && (
                                  <Button
                                    variant="outlined" size="small"
                                    style={{ borderColor: "#7c3aed", color: "#7c3aed", borderRadius: 20 }}
                                    disabled={aiGenerating || !campaignEditable}
                                    onClick={() => handleGenerateAiMessages(setFieldValue)}
                                  >
                                    {i18n.t("campaigns.kanbanSegment.aiRegenerate")}
                                  </Button>
                                )}
                                {aiMessagesGenerated && (
                                  <Chip
                                    label={i18n.t("campaigns.kanbanSegment.aiGeneratedSuccess")}
                                    size="small"
                                    style={{ background: "#10b981", color: "white" }}
                                  />
                                )}
                              </Box>
                            </Grid>

                          </Grid>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>

                  {/* SEÇÃO DE MENSAGENS / TEMPLATES */}
                  {sendMode === 'official' && (
                    <Grid xs={12} item>
                      <Box mb={2} p={2} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: 8, border: '1px solid #10b981' }}>
                        <Typography variant="body2" style={{ color: '#10b981' }}>
                          {i18n.t("campaigns.dialog.form.officialApiModeInfo")}
                        </Typography>
                        <FormControl variant="outlined" margin="dense" fullWidth style={{ marginTop: 10 }}>
                          <InputLabel id="template-label">{i18n.t("campaigns.dialog.form.templateLabel")}</InputLabel>
                          <Field
                            as={Select}
                            label={i18n.t("campaigns.dialog.form.templateLabel")}
                            labelId="template-label"
                            name="templateId"
                            disabled={!campaignEditable}
                          >
                            <MenuItem value="">Selecciona una plantilla aprobada</MenuItem>
                            {templates.map(t => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.shortcode}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Box>
                    </Grid>
                  )}
                  {sendMode !== 'official' && (
                    <>
                  <Grid xs={12} item>
                    <Box mb={2} p={2} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: 8, border: '1px solid #3b82f6' }}>
                      <Typography variant="body2" style={{ color: '#3b82f6', display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '1.2rem', marginRight: 8 }}>🤖</span>
                        <strong>{i18n.t("campaigns.kanbanSegment.antiBanTitle")}</strong>&nbsp; {i18n.t("campaigns.kanbanSegment.antiBanDesc")}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid xs={12} item>
                    <Box style={{ paddingTop: 10, border: "none" }}>
                      {values.confirmation ? (
                        <Grid container spacing={3} alignItems="flex-start">
                          {/* Columna Izquierda: Redacción + Opciones Editables */}
                          <Grid item xs={12} md={7}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                              ✍️ {i18n.t("campaigns.dialog.form.confirmationMessage1")}
                            </Typography>
                            {renderConfirmationMessageField("confirmationMessage1", i18n.t("campaigns.dialog.form.messagePlaceholder"))}

                            {/* Bloque de Opciones de Respuesta Guiada */}
                            <Box
                              style={{
                                marginTop: 16,
                                padding: 16,
                                borderRadius: 12,
                                border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #bfdbfe',
                                backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc',
                                boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                style={{
                                  fontWeight: 'bold',
                                  color: isDark ? '#60a5fa' : '#1d4ed8',
                                  marginBottom: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <span>🎯</span> {i18n.t("campaigns.dialog.form.guidedAnswersTitle")}
                              </Typography>
                              
                              {/* Micro-ayuda destacada con foco 💡 */}
                              <Alert
                                severity="info"
                                icon={false}
                                style={{
                                  padding: '8px 12px',
                                  marginBottom: 14,
                                  borderRadius: 8,
                                  backgroundColor: isDark ? 'rgba(30, 41, 59, 0.8)' : '#eff6ff',
                                  border: isDark ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid #bfdbfe',
                                  fontSize: '0.84rem',
                                  color: isDark ? '#bfdbfe' : '#1e40af',
                                  lineHeight: 1.45
                                }}
                              >
                                {i18n.t("campaigns.dialog.form.guidedAnswersHelp")}
                              </Alert>

                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label={i18n.t("campaigns.dialog.form.guidedOption1Label")}
                                    placeholder={i18n.t("campaigns.dialog.form.guidedOption1Placeholder")}
                                    value={guidedOption1}
                                    onChange={(e) => setGuidedOption1(e.target.value)}
                                    disabled={!campaignEditable && campaign.status !== "CANCELADA"}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <span style={{ fontSize: '0.95rem' }}>👉</span>
                                        </InputAdornment>
                                      ),
                                      style: {
                                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        color: isDark ? '#f1f5f9' : '#0f172a'
                                      }
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label={i18n.t("campaigns.dialog.form.guidedOption2Label")}
                                    placeholder={i18n.t("campaigns.dialog.form.guidedOption2Placeholder")}
                                    value={guidedOption2}
                                    onChange={(e) => setGuidedOption2(e.target.value)}
                                    disabled={!campaignEditable && campaign.status !== "CANCELADA"}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <span style={{ fontSize: '0.95rem' }}>👉</span>
                                        </InputAdornment>
                                      ),
                                      style: {
                                        backgroundColor: isDark ? '#1e293b' : '#ffffff',
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        color: isDark ? '#f1f5f9' : '#0f172a'
                                      }
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          </Grid>

                          {/* Columna Derecha: Vista Previa Lateral en Vivo (WhatsApp Chat Bubble) */}
                          <Grid item xs={12} md={5}>
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center' }}>
                              📱 {i18n.t("campaigns.dialog.form.livePreviewTitle")}
                            </Typography>
                            <Paper
                              elevation={0}
                              style={{
                                padding: 16,
                                borderRadius: 12,
                                backgroundColor: '#0b141a', // Fondo chat WhatsApp oscuro
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                minHeight: 280,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start'
                              }}
                            >
                              <Box
                                style={{
                                  backgroundColor: '#005c4b', // Burbuja saliente de WhatsApp
                                  color: '#e9edef',
                                  borderRadius: '10px 10px 2px 10px',
                                  padding: '12px 14px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                  fontSize: '0.86rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontFamily: 'Segoe UI, Helvetica, Arial, sans-serif'
                                }}
                              >
                                <div>
                                  {resolveSpintaxPreview(values.confirmationMessage1)}
                                </div>
                                
                                <Box style={{ marginTop: 14 }}>
                                  <div style={{ color: '#e9edef', marginBottom: 3, fontSize: '0.86rem' }}>
                                    {i18n.t("campaigns.dialog.form.livePreviewWritePrompt")}
                                  </div>
                                  <div style={{ fontWeight: 'bold', color: '#25d366', fontSize: '0.88rem' }}>
                                    👉 {(guidedOption1 && guidedOption1.trim()) ? guidedOption1.trim() : "SÍ, QUIERO UNIRME"}
                                  </div>
                                  <div style={{ color: '#e9edef', margin: '6px 0 3px 0', fontSize: '0.86rem' }}>
                                    {i18n.t("campaigns.dialog.form.livePreviewOrAlso")}
                                  </div>
                                  <div style={{ fontWeight: 'bold', color: '#f87171', fontSize: '0.88rem' }}>
                                    👉 {(guidedOption2 && guidedOption2.trim()) ? guidedOption2.trim() : "NO, GRACIAS"}
                                  </div>
                                </Box>

                                <Box display="flex" justifyContent="flex-end" alignItems="center" marginTop={1}>
                                  <span style={{ fontSize: '0.7rem', color: '#8696a0', marginRight: 4 }}>
                                    {moment().format("HH:mm")}
                                  </span>
                                  <DoneAllIcon style={{ fontSize: 14, color: '#53bdeb' }} />
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>
                      ) : (
                        (() => {
                          const directMsgs = [values.message1, values.message2, values.message3, values.message4, values.message5];
                          const antiBanReport = evaluate5MessagesVariation(directMsgs);

                          return (
                            <Box style={{ marginTop: 10, marginBottom: 16 }}>
                              {/* Cabecera con Título del Módulo y Conteo */}
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700, color: isDark ? '#93c5fd' : '#1e3a8a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span>🔄</span> {i18n.t("campaigns.dialog.form.directCampaignTabsTitle")}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={i18n.t("campaigns.dialog.form.readyCount", { filled: antiBanReport.filledCount, total: 5 })}
                                  style={{
                                    backgroundColor: antiBanReport.isComplete ? '#10b981' : '#f59e0b',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </Box>

                              {/* Pestañas de los 5 Mensajes */}
                              <Paper square style={{ backgroundColor: isDark ? '#1e293b' : '#f8fafc', borderRadius: '10px 10px 0 0', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0' }}>
                                <Tabs
                                  value={messageTab}
                                  onChange={(_, newTab) => setMessageTab(newTab)}
                                  indicatorColor="primary"
                                  textColor="primary"
                                  variant="fullWidth"
                                >
                                  {[1, 2, 3, 4, 5].map((num, idx) => {
                                    const text = (values[`message${num}`] || "").trim();
                                    const isLen = text.length >= 20;
                                    const hasConflict = antiBanReport.conflicts.some(c => c.indexA === idx || c.indexB === idx);
                                    let icon = "⚪";
                                    if (hasConflict) icon = "⚠️";
                                    else if (isLen) icon = "✅";

                                    return (
                                      <Tab
                                        key={num}
                                        label={
                                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', fontWeight: 600 }}>
                                            <span>{icon}</span> {i18n.t("campaigns.dialog.form.msgTabLabel", { number: num })}
                                          </span>
                                        }
                                      />
                                    );
                                  })}
                                </Tabs>
                              </Paper>

                              {/* Contenedor del Editor de la Pestaña Activa */}
                              <Box
                                style={{
                                  padding: 16,
                                  border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                                  borderTop: 'none',
                                  borderRadius: '0 0 10px 10px',
                                  backgroundColor: isDark ? '#0f172a' : '#ffffff'
                                }}
                              >
                                {messageTab === 0 && renderMessageField("message1", i18n.t("campaigns.dialog.form.alternativeNumber", { number: 1 }))}
                                {messageTab === 1 && renderMessageField("message2", i18n.t("campaigns.dialog.form.alternativeNumber", { number: 2 }))}
                                {messageTab === 2 && renderMessageField("message3", i18n.t("campaigns.dialog.form.alternativeNumber", { number: 3 }))}
                                {messageTab === 3 && renderMessageField("message4", i18n.t("campaigns.dialog.form.alternativeNumber", { number: 4 }))}
                                {messageTab === 4 && renderMessageField("message5", i18n.t("campaigns.dialog.form.alternativeNumber", { number: 5 }))}
                              </Box>

                              {/* Escudo de Seguridad Anti-Ban (Barra y Alertas) */}
                              <Box mt={2}>
                                {!antiBanReport.isComplete ? (
                                  <Alert severity="warning" style={{ borderRadius: 8 }}>
                                    <AlertTitle><strong>{i18n.t("campaigns.dialog.form.antiBanGaugeTitle")}</strong></AlertTitle>
                                    {i18n.t("campaigns.dialog.form.antiBanGaugeIncomplete", { filled: antiBanReport.filledCount })}
                                  </Alert>
                                ) : antiBanReport.conflicts.length > 0 ? (
                                  <Alert severity="error" style={{ borderRadius: 8 }}>
                                    <AlertTitle><strong>{i18n.t("campaigns.dialog.form.antiBanGaugeTitle")}</strong></AlertTitle>
                                    {i18n.t("campaigns.dialog.form.antiBanGaugeConflict", {
                                      indexA: antiBanReport.conflicts[0].indexA + 1,
                                      indexB: antiBanReport.conflicts[0].indexB + 1,
                                      sim: antiBanReport.conflicts[0].similarity
                                    })}
                                  </Alert>
                                ) : (
                                  <Alert severity="success" style={{ borderRadius: 8, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                                    <AlertTitle><strong>{i18n.t("campaigns.dialog.form.antiBanGaugeTitle")}</strong></AlertTitle>
                                    {i18n.t("campaigns.dialog.form.antiBanGaugeSuccess", { avg: antiBanReport.avgDifference })}
                                  </Alert>
                                )}
                              </Box>
                            </Box>
                          );
                        })()
                      )}
                      
                      {!values.confirmation && (
                        <Grid xs={12} item style={{ marginTop: 20 }}>
                          <Alert severity="error" style={{ borderRadius: 8 }}>
                            <AlertTitle>{i18n.t("campaigns.dialog.form.directCampaignAlertTitle")}</AlertTitle>
                            <div dangerouslySetInnerHTML={{ __html: i18n.t("campaigns.dialog.form.directCampaignAlertBody") }} />
                          </Alert>
                        </Grid>
                      )}
                      
                      <Grid container spacing={2} style={{ marginTop: 20 }}>
                        <Grid item xs={12} md={6}>
                          <Alert severity="info" style={{ borderRadius: 8, height: '100%', '& p': { margin: '4px 0', fontSize: '0.85rem' } }}>
                            <AlertTitle><strong>{i18n.t("campaigns.dialog.form.spintaxGuideTitle")}</strong></AlertTitle>
                            <div style={{ fontSize: '0.88rem' }}>
                              <p style={{ margin: '4px 0' }} dangerouslySetInnerHTML={{ __html: i18n.t("campaigns.dialog.form.spintaxGuideDesc1") }} />
                              <p style={{ margin: '4px 0' }} dangerouslySetInnerHTML={{ __html: i18n.t("campaigns.dialog.form.spintaxGuideExample") }} />
                              <p style={{ margin: '4px 0', marginTop: '12px' }} dangerouslySetInnerHTML={{ __html: i18n.t("campaigns.dialog.form.spintaxGuideDesc2") }} />
                              <p style={{ margin: '4px 0' }} dangerouslySetInnerHTML={{ __html: i18n.t("campaigns.dialog.form.spintaxGuideDesc3") }} />
                            </div>
                          </Alert>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Alert severity="success" icon={false} style={{ borderRadius: 8, height: '100%', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                            <AlertTitle><strong>{i18n.t("campaigns.dialog.form.spintaxExampleTitle")}</strong></AlertTitle>
                            <div style={{ fontSize: "0.85rem", whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                                {i18n.t("campaigns.dialog.form.spintaxExampleBody")}
                              </div>
                          </Alert>
                        </Grid>
                      </Grid>
</Box>
                  </Grid>

                  {/* ── VISTA PREVIA DE LOS 5 MENSAJES ROTATIVOS (Campañas Directas) ────────────────── */}
                  {!values.confirmation && sendMode !== "official" && (
                    <Grid xs={12} item>
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ borderRadius: 20, borderColor: "#6366f1", color: "#6366f1", marginBottom: 8 }}
                        onClick={() => setShowPreview(prev => !prev)}
                        startIcon={<VisibilityIcon style={{ fontSize: 16 }} />}
                      >
                        {showPreview ? i18n.t("campaigns.kanbanSegment.hidePreview") : i18n.t("campaigns.kanbanSegment.showPreview")}
                      </Button>

                      <Collapse in={showPreview}>
                      <Box style={{ background: "#f8f8ff", border: "1px solid #e0e0f0", borderRadius: 10, padding: 16, marginBottom: 8 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12, color: "#4b5563" }}>
                          {i18n.t("campaigns.kanbanSegment.previewTitle")}
                        </Typography>


                        {/* Grid de los 5 mensajes */}
                        <Grid container spacing={2}>
                          {[
                            { key: "message1", num: 1 },
                            { key: "message2", num: 2 },
                            { key: "message3", num: 3 },
                            { key: "message4", num: 4 },
                            { key: "message5", num: 5 },
                          ].map(({ key, num }) => {
                            const label = i18n.t("campaigns.dialog.form.msgCardLabel", { number: num });
                            const rawMsg = (values[key] || "").trim();
                            const chunks = splitCampaignMessage(rawMsg);
                            const totalChars = rawMsg.length;
                            const splitCount = chunks.length;
                            return (
                              <Grid item xs={12} md={6} key={key}>
                                <Box style={{
                                  background: "white",
                                  borderRadius: 10,
                                  border: "1px solid #e0e0e0",
                                  overflow: "hidden",
                                }}>
                                  {/* Header tipo WhatsApp */}
                                  <Box style={{ background: "#25d366", padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Typography variant="caption" style={{ color: "white", fontWeight: 700 }}>
                                      {label}
                                    </Typography>
                                    {splitCount > 1 && (
                                      <Typography variant="caption" style={{ color: "#e0ffe0", fontSize: 10 }}>
                                        {i18n.t("campaigns.dialog.form.waMessagesCount", { count: splitCount })}
                                      </Typography>
                                    )}
                                  </Box>
                                  {/* Cuerpo: una burbuja por chunk */}
                                  <Box style={{ padding: "10px 12px", minHeight: 60, display: "flex", flexDirection: "column", gap: 8 }}>
                                    {rawMsg ? chunks.map((chunk, ci) => (
                                      <Box key={ci} style={{
                                        background: ci === 0 ? "#dcf8c6" : "#f0f0f0",
                                        borderRadius: 8,
                                        padding: "6px 10px",
                                        position: "relative"
                                      }}>
                                        {splitCount > 1 && (
                                          <Typography variant="caption" style={{ fontSize: 9, color: "#888", display: "block", marginBottom: 2 }}>
                                            {i18n.t("campaigns.dialog.form.messageChunkIndicator", { current: ci + 1, total: splitCount })}
                                          </Typography>
                                        )}
                                        <Typography variant="body2" style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "#111" }}>
                                          {chunk}
                                        </Typography>
                                        <Typography variant="caption" style={{ fontSize: 10, color: chunk.length > 700 ? "#ef4444" : "#aaa", float: "right" }}>
                                          {chunk.length} chars
                                        </Typography>
                                      </Box>
                                    )) : (
                                      <Typography variant="body2" style={{ color: "#aaa", fontStyle: "italic", fontSize: 12 }}>
                                        {i18n.t("campaigns.dialog.form.emptyMessage")}
                                      </Typography>
                                    )}
                                  </Box>
                                  {/* Footer total */}
                                  <Box style={{ padding: "4px 12px", background: "#f9f9f9", borderTop: "1px solid #f0f0f0" }}>
                                    <Typography variant="caption" style={{ color: totalChars > 800 ? "#ef4444" : "#9ca3af" }}>
                                      {i18n.t("campaigns.dialog.form.totalCharsCount", { chars: totalChars })} {splitCount > 1 ? i18n.t("campaigns.dialog.form.totalChunksCount", { chunks: splitCount }) : ""}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Grid>
                  )}
                  </>
                  )}
                  

                </Grid>
              </DialogContent>
              <DialogActions className={classes.dialogActionsPadded}>
                {campaign.status === "CANCELADA" && (
                  <Button
                    onClick={() => restartCampaign()}
                    className={classes.secondaryButton}
                  >
                    {i18n.t("campaigns.dialog.buttons.restart")}
                  </Button>
                )}
                {campaign.status === "EM_ANDAMENTO" && (
                  <Button
                    onClick={() => cancelCampaign()}
                    className={classes.secondaryButton}
                  >
                    {i18n.t("campaigns.dialog.buttons.cancel")}
                  </Button>
                )}
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={classes.secondaryButton}
                >
                  {i18n.t("campaigns.dialog.buttons.close")}
                </Button>
                {(campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className={classes.applyButton}
                    onClick={async () => {
                      const currentErrors = await validateForm();
                      if (Object.keys(currentErrors).length > 0) {
                        const errorMessages = Object.values(currentErrors).join(', ');
                        toast.error(`Faltan completar campos obligatorios: ${errorMessages}`);
                      }
                    }}
                  >
                    {campaignId
                      ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                      : `${i18n.t("campaigns.dialog.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={20}
                        style={{ marginLeft: 8 }}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog >

      {/* MODAL DE LISTADO DE CONTACTOS [ANTIGRAVITY] */}
      <Dialog open={showContactsModal} onClose={() => setShowContactsModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contactos que recibirán la campaña</DialogTitle>
        <DialogContent dividers>
          <List dense>
            {contactsPreview.contacts.map((contact) => (
              <ListItem key={contact.id} divider>
                <ListItemText
                  primary={contact.name || "Sin nombre"}
                  secondary={contact.number}
                />
              </ListItem>
            ))}
            {contactsPreview.count > 50 && (
              <ListItem>
                <ListItemText
                  primary="..."
                  secondary={`Y ${contactsPreview.count - 50} contactos más.`}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactsModal(false)} color="primary">Cerrar</Button>
        </DialogActions>
      </Dialog>
      {/* [ANTIGRAVITY] Modal Saldo Insuficiente IA */}
      <Dialog open={financeAlertOpen} onClose={() => setFinanceAlertOpen(false)}>
        <DialogTitle style={{ color: "#ef4444" }}>Saldo Insuficiente</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            No tienes saldo suficiente para generar mensajes con IA. 
            El costo es de $0.05 por generación. 
            Por favor, recarga tu saldo en la sección Financiero.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinanceAlertOpen(false)} color="primary" variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default CampaignModal;
 
